import { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { Phone, Menu, X, ChevronDown } from 'lucide-react'
import { NAV, SITE } from '../constants/site'

export function Navbar() {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => setOpen(false), [location.pathname, location.hash, location.key])

  const linkBase = 'text-[0.78rem] font-semibold uppercase tracking-[0.12em] transition-colors'

  return (
    <header
      className={`sticky top-0 z-50 border-b border-white/10 bg-night transition-all duration-300 ${
        scrolled ? 'shadow-[0_8px_30px_rgba(0,0,0,0.55)]' : ''
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-2.5 lg:px-8">
        {/* Logo + depuis 1925 */}
        <Link to="/" className="flex items-center gap-3.5 sm:gap-4" aria-label="Maison Gilles — Accueil">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full border border-white/30 sm:h-16 sm:w-16">
            <img src="/img/logo-silhouette.png" alt="" className="h-9 w-auto sm:h-10" />
          </span>
          <div className="leading-none">
            <img src="/img/logo-complet.png" alt="Maison Gilles SA" className="h-6 w-auto sm:h-7" />
            <div className="mt-2 flex items-center gap-2 text-[0.6rem] font-bold uppercase tracking-[0.32em] text-orange sm:text-[0.68rem]">
              <span className="h-px w-5 bg-orange/60" /> depuis {SITE.depuis} <span className="h-px w-5 bg-orange/60" />
            </div>
          </div>
        </Link>

        {/* Menu desktop */}
        <ul className="hidden items-center gap-7 lg:flex">
          {NAV.map((item) => (
            <li key={item.label} className="group relative">
              {item.children ? (
                <>
                  <Link to={item.to ?? item.children[0].to} className={`${linkBase} flex items-center gap-1 text-white/75 hover:text-white`}>
                    {item.label}
                    <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                  </Link>
                  <ul className="invisible absolute left-1/2 top-full z-10 w-56 -translate-x-1/2 translate-y-2 rounded-2xl border border-white/10 bg-night-lt p-2 opacity-0 shadow-2xl transition-all duration-200 group-hover:visible group-hover:translate-y-1 group-hover:opacity-100">
                    {item.children.map((c) => (
                      <li key={c.to}>
                        <NavLink
                          to={c.to}
                          end
                          className={({ isActive }) =>
                            `block rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                              isActive ? 'bg-orange text-white' : 'text-white/75 hover:bg-white/10'
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </>
              ) : item.to!.includes('#') ? (
                <Link to={item.to!} className={`${linkBase} text-white/75 hover:text-white`}>
                  {item.label}
                </Link>
              ) : (
                <NavLink
                  to={item.to!}
                  end
                  className={({ isActive }) =>
                    `${linkBase} relative after:absolute after:-bottom-2 after:left-0 after:h-0.5 after:w-full after:origin-left after:bg-orange after:transition-transform after:duration-300 ${
                      isActive ? 'text-white after:scale-x-100' : 'text-white/75 hover:text-white after:scale-x-0 hover:after:scale-x-100'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )}
            </li>
          ))}
        </ul>

        {/* CTA téléphone */}
        <a
          href={`tel:${SITE.telephoneLien}`}
          className="hidden items-center gap-2 rounded-full bg-orange px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-orange/20 transition-colors hover:bg-orange-dk lg:inline-flex"
        >
          <Phone className="h-4 w-4" strokeWidth={2.4} />
          {SITE.telephone}
        </a>

        {/* Burger mobile */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="grid h-10 w-10 place-items-center rounded-full text-white transition-colors hover:bg-white/10 lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Menu mobile */}
      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-white/10 bg-night px-5 pb-6 pt-2 lg:hidden">
          <ul className="flex flex-col">
            {NAV.map((item) => (
              <li key={item.label} className="border-b border-white/10">
                {item.to && (
                  <NavLink to={item.to} end className="block py-3 text-sm font-semibold uppercase tracking-wider text-white">
                    {item.label}
                  </NavLink>
                )}
                {!item.to && <p className="pt-3 text-sm font-semibold uppercase tracking-wider text-white">{item.label}</p>}
                {item.children && (
                  <ul className="pb-2 pl-3">
                    {item.children.map((c) => (
                      <li key={c.to}>
                        <NavLink to={c.to} end className="block py-2 text-sm text-white/65">
                          {c.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
          <a href={`tel:${SITE.telephoneLien}`} className="mt-5 flex items-center justify-center gap-2 rounded-full bg-orange px-4 py-3 font-bold text-white">
            <Phone className="h-4 w-4" /> {SITE.telephone}
          </a>
          <Link to="/login" className="mt-3 block text-center text-xs uppercase tracking-widest text-white/40">
            Espace administrateur
          </Link>
        </div>
      )}
    </header>
  )
}
